import { useCallback, useEffect, useRef, useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { initOneSignal } from "@/lib/onesignal";
import { SoundProvider } from "@/context/SoundContext";
import { MiniPlayer } from "@/components/MiniPlayer";
import { Home, Database, Settings as SettingsIcon, Activity, Lock, Radar } from "lucide-react";

const BULL_ICON = "/smartfx-logo.png";

function GoatFab({ onOpen, iconSrc }) {
  const ref = useRef(null);
  const drag = useRef({ active: false, moved: false, offX: 0, offY: 0 });
  const [pos, setPos] = useState(() => {
    try { const s = JSON.parse(localStorage.getItem("sfx_goat_pos")); if (s && typeof s.x === "number") return s; } catch { /* ignore */ }
    return null; // null => default CSS bottom-right placement
  });

  const clamp = (x, y) => {
    const size = 52, pad = 8;
    const maxX = window.innerWidth - size - pad;
    const maxY = window.innerHeight - size - pad;
    return { x: Math.max(pad, Math.min(x, maxX)), y: Math.max(pad, Math.min(y, maxY)) };
  };

  const onDown = (e) => {
    const rect = ref.current.getBoundingClientRect();
    drag.current = { active: true, moved: false, offX: e.clientX - rect.left, offY: e.clientY - rect.top, startX: e.clientX, startY: e.clientY };
    ref.current.setPointerCapture(e.pointerId);
  };
  const onMove = (e) => {
    if (!drag.current.active) return;
    const dx = e.clientX - drag.current.startX;
    const dy = e.clientY - drag.current.startY;
    // Only treat as a DRAG once the pointer moves past ~8px, so a normal tap
    // (which always jitters a pixel or two on touch screens) still opens the panel.
    if (!drag.current.moved && (dx * dx + dy * dy) < 64) return;
    drag.current.moved = true;
    setPos(clamp(e.clientX - drag.current.offX, e.clientY - drag.current.offY));
  };
  const onUp = () => {
    if (!drag.current.active) return;
    drag.current.active = false;
    if (drag.current.moved) {
      setPos((p) => { if (p) localStorage.setItem("sfx_goat_pos", JSON.stringify(p)); return p; });
    } else {
      onOpen();
    }
  };

  const style = {
    boxShadow: "0 0 30px var(--sfx-accent-glow)",
    background: "radial-gradient(circle at 50% 40%, #0b1a2e, #060a14)",
    border: "2px solid var(--sfx-accent)",
    touchAction: "none",
    ...(pos ? { left: pos.x, top: pos.y } : { right: 16, bottom: 96 }),
  };

  return (
    <button ref={ref} data-testid="goat-ai-icon"
      onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp}
      className="fixed z-50 h-13 w-13 rounded-full flex items-center justify-center overflow-hidden cursor-grab active:cursor-grabbing transition-transform duration-200 hover:scale-110 active:scale-95"
      style={{ ...style, height: 52, width: 52 }}>
      <img src={iconSrc || BULL_ICON} alt="Live Trading Activity" draggable={false} className="h-full w-full object-contain pointer-events-none" style={{ transform: "scale(1.0)" }} />
    </button>
  );
}

export default function DashboardLayout() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [account, setAccount] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [bots, setBots] = useState([]);
  const [entitlements, setEntitlements] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aiIcon, setAiIcon] = useState(null);

  const refreshAll = useCallback(async () => {
    try {
      const [a, b, ac, ent, br] = await Promise.all([
        api.get("/account"), api.get("/bots"), api.get("/accounts"), api.get("/entitlements"),
        api.get("/branding").catch(() => ({ data: {} })),
      ]);
      setAccount(a.data);
      setBots(b.data);
      setAccounts(ac.data || []);
      setEntitlements(ent.data);
      setAiIcon(br.data?.ai_icon_url || null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refreshAll(); }, [refreshAll]);

  // Keep entitlements/plan fresh so a client sees an admin-granted plan WITHOUT re-login:
  // poll periodically and refresh when the app regains focus / becomes visible.
  useEffect(() => {
    const poll = setInterval(refreshAll, 45000);
    const onFocus = () => refreshAll();
    const onVisible = () => { if (document.visibilityState === "visible") refreshAll(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(poll);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [refreshAll]);

  useEffect(() => { if (user?.id) initOneSignal(user.id); }, [user?.id]);

  const runningBot = bots.find((b) => b.status === "RUNNING");

  // Paywall: clients with NO active subscription are fully blocked until an admin approves
  // their payment. Staff (admin/manager) always have access. Billing page stays reachable.
  const isClient = user?.role !== "admin" && user?.role !== "manager";
  const locked = !loading && entitlements && entitlements.active === false && isClient;
  const onBilling = location.pathname.startsWith("/app/billing");
  const showPaywall = locked && !onBilling;

  const tabs = [
    { to: "/app", icon: Home, label: "Home", end: true, testid: "nav-home" },
    { to: "/app/account", icon: Database, label: "Metatrader", testid: "nav-account" },
    { to: "/app/signals", icon: Radar, label: "Signal", testid: "nav-signals" },
    { to: "/app/cpi", icon: Activity, label: "CPI", testid: "nav-cpi" },
    { to: "/app/settings", icon: SettingsIcon, label: "Settings", testid: "nav-settings" },
  ];

  return (
    <SoundProvider>
    <div className="min-h-screen w-full bg-[#04060d] md:flex">
      {/* Desktop / tablet sidebar navigation (hidden on phones) */}
      <aside className="hidden md:flex md:flex-col md:w-60 lg:w-64 md:shrink-0 md:sticky md:top-0 md:h-screen bg-[#060a14] border-r border-[#14233d] z-40">
        <div className="flex items-center gap-2.5 px-5 h-16 border-b border-[#14233d]">
          <img src={BULL_ICON} alt="Smart FX" className="h-8 w-8 object-contain" />
          <span className="font-heading font-bold text-white tracking-tight">Smart FX</span>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1">
          {tabs.map((t) => (
            <NavLink key={t.to} to={t.to} end={t.end} data-testid={`side-${t.testid}`}
              style={({ isActive }) => (isActive ? { color: "var(--sfx-accent)", backgroundColor: "rgba(28,140,255,0.08)" } : undefined)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${isActive ? "" : "text-[#7f9bc4] hover:text-white hover:bg-white/5"}`}>
              <t.icon className="h-5 w-5 shrink-0" />
              <span>{t.label}</span>
            </NavLink>
          ))}
        </nav>
        {user && (
          <div className="px-4 py-3 border-t border-[#14233d] text-xs text-[#5f7ba6] truncate">
            {user.name || user.username}
          </div>
        )}
      </aside>

      {/* Main column */}
      <div className="relative flex-1 min-w-0 flex justify-center bg-black md:bg-transparent">
        <div className="relative w-full max-w-[480px] md:max-w-none min-h-screen bg-[#04060d] overflow-x-hidden pb-24 md:pb-10">
          <main className="min-h-screen md:px-8 md:py-6">
            <div className="md:max-w-3xl lg:max-w-4xl md:mx-auto">
          {showPaywall ? (
            <div className="px-6 pt-20 pb-10 flex flex-col items-center text-center fade-up" data-testid="paywall-locked">
              <div className="h-16 w-16 rounded-2xl flex items-center justify-center mb-6"
                style={{ background: "radial-gradient(circle at 50% 40%, #0b1a2e, #060a14)", border: "2px solid var(--sfx-accent)", boxShadow: "0 0 30px var(--sfx-accent-glow)" }}>
                <Lock className="h-7 w-7" style={{ color: "var(--sfx-accent)" }} />
              </div>
              <h1 className="font-heading text-2xl font-bold text-white mb-2">Aksè fèmen</h1>
              <p className="text-sm text-[#8fb4e6] max-w-xs leading-relaxed mb-1">
                Ou bezwen yon abònman aktif pou w itilize Smart Scalper Fx.
              </p>
              <p className="text-xs text-[#5f7ba6] max-w-xs leading-relaxed mb-7">
                Chwazi yon plan epi voye peman ou. Yon administratè ap apwouve li, epi timer plan an
                ap kòmanse konte lè li apwouve.
              </p>
              <button onClick={() => navigate("/app/billing")} data-testid="paywall-subscribe-btn"
                className="h-12 px-8 rounded-full font-heading font-semibold text-black transition-transform hover:scale-105 active:scale-95"
                style={{ backgroundColor: "var(--sfx-accent)" }}>
                Chwazi yon plan · Peye
              </button>
            </div>
          ) : (
            <Outlet context={{ account, accounts, bots, loading, refreshAll, runningBot, user, entitlements }} />
          )}
            </div>
          </main>

          {runningBot && !showPaywall && <GoatFab onOpen={() => navigate("/app/ai-activity")} iconSrc={aiIcon ? `${process.env.REACT_APP_BACKEND_URL}${aiIcon}` : null} />}

          <MiniPlayer />

          {/* Bottom tab bar — phones only */}
          <nav className="md:hidden fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-[480px] bg-[#060a14]/95 backdrop-blur-xl border-t border-[#14233d] px-4 py-3 flex items-center justify-around z-40">
            {tabs.map((t) => (
              <NavLink key={t.to} to={t.to} end={t.end} data-testid={t.testid}
                style={({ isActive }) => (isActive ? { color: "var(--sfx-accent)" } : undefined)}
                className={({ isActive }) =>
                  `flex flex-col items-center gap-1 transition-colors ${isActive ? "" : "text-[#5f7ba6] hover:text-white"}`}>
                <t.icon className="h-6 w-6" />
                <span className="text-[11px] font-medium">{t.label}</span>
              </NavLink>
            ))}
          </nav>
        </div>
      </div>
    </div>
    </SoundProvider>
  );
}
